import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  TextField,
  Tab,
  Tabs,
  Chip,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  Help as HelpIcon,
  Email as EmailIcon,
  Gesture as DrawIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  Crop as CropIcon,
  RestartAlt as ResetIcon
} from '@mui/icons-material';
import SignatureCanvas from 'react-signature-canvas';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { sendEmail } from '../services/emailService';
import { PDFViewer, BlobProvider } from '@react-pdf/renderer';
import ActaPDF from './ActaPDF';

const SignatureManager = ({ 
  open, 
  onClose, 
  acta, 
  onSignatureUpdate 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedAttendee, setSelectedAttendee] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showActaPreview, setShowActaPreview] = useState(false);
  const [signatures, setSignatures] = useState({});
  const [shareableUrl, setShareableUrl] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showTutorial, setShowTutorial] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [signMethod, setSignMethod] = useState(0); // 0: Draw, 1: Upload
  const [uploadedImage, setUploadedImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const signatureRef = useRef();
  const fileInputRef = useRef();

  // Cargar firmas existentes al abrir el modal
  useEffect(() => {
    if (open && acta) {
      loadExistingSignatures();
      generateShareableUrl();
    }
  }, [open, acta]);

  const loadExistingSignatures = () => {
    const existingSignatures = {};
    if (acta.asistentes) {
      acta.asistentes.forEach((asistente, index) => {
        if (asistente.firma) {
          existingSignatures[index] = asistente.firma;
        }
      });
    }
    setSignatures(existingSignatures);
  };

  const generateShareableUrl = () => {
    if (acta) {
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/firma/${acta.id}`;
      setShareableUrl(shareUrl);
    }
  };

  const handleAttendeeSelect = (attendeeIndex) => {
    setSelectedAttendee(attendeeIndex);
    setShowSignaturePad(true);
    setSignMethod(0); // Reset to draw mode
    setUploadedImage(null); // Reset uploaded image
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setSnackbar({
          open: true,
          message: 'La imagen no debe superar los 2MB',
          severity: 'warning'
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
        setOriginalImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveUploadedImage = () => {
    setUploadedImage(null);
    setOriginalImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRotate = async (direction) => {
    if (!uploadedImage) return;

    const img = new Image();
    img.src = uploadedImage;
    await new Promise(resolve => img.onload = resolve);

    const canvas = document.createElement('canvas');
    if (Math.abs(direction) === 90) {
      canvas.width = img.height;
      canvas.height = img.width;
    } else {
      canvas.width = img.width;
      canvas.height = img.height;
    }

    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((direction * Math.PI) / 180);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);

    setUploadedImage(canvas.toDataURL());
  };

  const handleAutoCrop = async () => {
    if (!uploadedImage) return;

    const img = new Image();
    img.src = uploadedImage;
    await new Promise(resolve => img.onload = resolve);

    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);

    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const l = pixels.data.length;
    const bound = { top: null, left: null, right: null, bottom: null };
    let x, y;

    for (let i = 0; i < l; i += 4) {
      const r = pixels.data[i];
      const g = pixels.data[i+1];
      const b = pixels.data[i+2];
      const a = pixels.data[i+3];

      // Considerar blanco si RGB > 230 y opaco
      const isWhite = r > 230 && g > 230 && b > 230;
      const isTransparent = a === 0;

      if (!isWhite && !isTransparent) {
        x = (i / 4) % canvas.width;
        y = ~~((i / 4) / canvas.width);

        if (bound.top === null) bound.top = y;
        if (bound.left === null) bound.left = x;
        if (bound.right === null) bound.right = x;
        if (bound.bottom === null) bound.bottom = y;

        if (y > bound.bottom) bound.bottom = y;
        if (x < bound.left) bound.left = x;
        if (x > bound.right) bound.right = x;
      }
    }

    if (bound.top !== null) {
      const padding = 10;
      const width = bound.right - bound.left + 1;
      const height = bound.bottom - bound.top + 1;

      const trimmedCanvas = document.createElement('canvas');
      trimmedCanvas.width = width + (padding * 2);
      trimmedCanvas.height = height + (padding * 2);
      const trimmedCtx = trimmedCanvas.getContext('2d');

      trimmedCtx.drawImage(
        canvas, 
        bound.left, bound.top, width, height, 
        padding, padding, width, height
      );

      setUploadedImage(trimmedCanvas.toDataURL());
      setSnackbar({
        open: true,
        message: 'Imagen ajustada automáticamente',
        severity: 'success'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'No se detectó contenido para recortar',
        severity: 'warning'
      });
    }
  };

  const handleResetImage = () => {
    if (originalImage) {
      setUploadedImage(originalImage);
    }
  };

  const handleSaveSignature = async () => {
    let signatureDataUrl;

    if (signMethod === 0) {
      // Drawing mode
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        setSnackbar({
          open: true,
          message: 'Por favor, dibuje su firma antes de guardar',
          severity: 'warning'
        });
        return;
      }
      signatureDataUrl = signatureRef.current.toDataURL();
    } else {
      // Upload mode
      if (!uploadedImage) {
        setSnackbar({
          open: true,
          message: 'Por favor, suba una imagen de su firma',
          severity: 'warning'
        });
        return;
      }
      signatureDataUrl = uploadedImage;
    }

    try {

      
      // Actualizar el estado local primero
      const newSignatures = {
        ...signatures,
        [selectedAttendee]: signatureDataUrl
      };
      setSignatures(newSignatures);

      // Obtener los datos más recientes del acta desde Firebase para evitar condiciones de carrera
      const actaRef = doc(db, 'actas', acta.id);
      const actaSnapshot = await getDoc(actaRef);
      
      if (!actaSnapshot.exists()) {
        throw new Error('El acta no existe');
      }
      
      const currentActaData = actaSnapshot.data();
      const currentAsistentes = currentActaData.asistentes || acta.asistentes || [];
      
      if (!currentAsistentes.length) {
        throw new Error('No hay asistentes registrados en el acta');
      }

      // Actualizar solo el asistente específico con la nueva firma
      const updatedAsistentes = currentAsistentes.map((asistente, index) => {
        if (index === parseInt(selectedAttendee)) {
          const updatedAsistente = { ...asistente, firma: signatureDataUrl, fechaFirma: new Date() };
          if (updatedAsistente.autoApproved) {
            delete updatedAsistente.autoApproved;
          }
          if (updatedAsistente.tipoFirma === 'auto_approved') {
            delete updatedAsistente.tipoFirma;
          }
          return updatedAsistente;
        }
        return asistente;
      });

      // Guardar en Firebase
      await updateDoc(actaRef, {
        asistentes: updatedAsistentes
      });


      setSnackbar({
        open: true,
        message: 'Firma guardada exitosamente',
        severity: 'success'
      });

      setShowSignaturePad(false);
      setSelectedAttendee('');
      
      // Notificar al componente padre con los datos actualizados
      if (onSignatureUpdate) {
        onSignatureUpdate(updatedAsistentes);
      }
    } catch (error) {
      console.error('Error al guardar la firma:', error);
      setSnackbar({
        open: true,
        message: 'Error al guardar la firma',
        severity: 'error'
      });
    }
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const copyShareableUrl = () => {
    navigator.clipboard.writeText(shareableUrl);
    setSnackbar({
      open: true,
      message: 'URL copiada al portapapeles',
      severity: 'success'
    });
  };

  const getAttendeeDisplayName = (asistente, index) => {
    return `${index + 1}. ${asistente.nombre} - ${asistente.cargo}`;
  };

  const handleUserSelection = (attendeeIndex) => {
    setSelectedUsers(prev => {
      if (prev.includes(attendeeIndex)) {
        return prev.filter(index => index !== attendeeIndex);
      } else {
        return [...prev, attendeeIndex];
      }
    });
  };

  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) {
        added++;
      }
    }
    return d;
  };

  const formatDateEs = (date) => new Date(date).toLocaleDateString('es-ES');

  const handleSendEmails = async () => {
    if (selectedUsers.length === 0) {
      setSnackbar({
        open: true,
        message: 'Seleccione al menos un usuario para enviar el correo',
        severity: 'warning'
      });
      return;
    }

    setEmailLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const userIndex of selectedUsers) {
      const asistente = acta.asistentes[userIndex];
      if (!asistente.email) {
        errorCount++;
        continue;
      }

      try {
        const subject = `[IETS GESTOR ACTAS] Acta creada para firmar #${acta.numeroActa} - ${acta.tipoActa}`;
        const baseDate = acta.fecha ? (acta.fecha.toDate ? acta.fecha.toDate() : new Date(acta.fecha)) : new Date();
        const deadlineDate = addBusinessDays(baseDate, 2);
        const deadlineStr = formatDateEs(deadlineDate);
        const html = `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #6164ff;">Acta Creada para Firmar</h2>
            <p>Estimado/a <strong>${asistente.nombre}</strong>,</p>
            <p>Se ha creado el acta <strong>${acta.numeroActa}</strong> y requiere su firma digital.</p>
            
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
              <h3 style="margin-top: 0;">Detalles del Acta:</h3>
              <ul>
                <li><strong>Número:</strong> ${acta.numeroActa}</li>
                <li><strong>Tipo de acta:</strong> ${acta.tipoActa || 'No especificado'}</li>
                <li><strong>Proyecto:</strong> ${acta.tipoProyecto || 'No especificado'}</li>
                <li><strong>Fecha:</strong> ${acta.fecha ? (acta.fecha.toDate ? acta.fecha.toDate().toLocaleDateString('es-ES') : new Date(acta.fecha).toLocaleDateString('es-ES')) : 'No especificada'}</li>
                <li><strong>Su cargo:</strong> ${asistente.cargo}</li>
              </ul>
            </div>
            
            <div style="background-color: #fff3e0; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #ff9800;">
              <p style="margin: 0; color: #e65100;"><strong>Importante:</strong> Tiene un plazo máximo de 2 días hábiles para firmar o enviar un comentario sobre este documento. <strong>Fecha límite:</strong> ${deadlineStr}. Si no se registra su firma dentro de este plazo, el acta se dará por aprobada automáticamente.</p>
            </div>

            <p>Para firmar digitalmente el acta, haga clic en el siguiente enlace:</p>
            
            <div style="text-align: center; margin: 25px 0;">
              <a href="${shareableUrl}" style="background-color: #6164ff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">✍️ Firmar Acta</a>
            </div>
            
            <p><strong>Instrucciones:</strong></p>
            <ol>
              <li>Haga clic en el enlace de arriba</li>
              <li>Busque su nombre en la lista de asistentes</li>
              <li>Haga clic en "Firmar" junto a su nombre</li>
              <li>Dibuje su firma en el área designada</li>
              <li>Guarde su firma</li>
            </ol>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #666;">
              <p>Este es un correo automático, por favor no responda a este mensaje.</p>
              <p>Instituto de Evaluación Tecnológica en Salud - IETS</p>
            </div>
          </div>
        `;

        const result = await sendEmail(asistente.email, subject, html);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error('Error enviando correo a:', asistente.email, error);
        errorCount++;
      }
    }

    setEmailLoading(false);
    setShowEmailDialog(false);
    setSelectedUsers([]);

    if (successCount > 0 && errorCount === 0) {
      setSnackbar({
        open: true,
        message: `Correos enviados exitosamente a ${successCount} usuario(s)`,
        severity: 'success'
      });
    } else if (successCount > 0 && errorCount > 0) {
      setSnackbar({
        open: true,
        message: `${successCount} correos enviados, ${errorCount} fallaron`,
        severity: 'warning'
      });
    } else {
      setSnackbar({
        open: true,
        message: 'Error al enviar los correos',
        severity: 'error'
      });
    }
  };

  const getAvailableUsers = () => {
    return acta?.asistentes?.filter((asistente, index) => {
      return asistente.email && !signatures[index];
    }) || [];
  };

  const getAvailableUserIndices = () => {
    return acta?.asistentes?.map((asistente, index) => {
      return asistente.email && !signatures[index] ? index : null;
    }).filter(index => index !== null) || [];
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#6164ff',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" component="div">
              Gestión de Firmas - Acta #{acta?.numeroActa}
            </Typography>
            <Tooltip title="Ver tutorial de firma">
              <IconButton 
                onClick={() => setShowTutorial(true)} 
                sx={{ color: 'white', ml: 1 }}
                size="small"
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* URL Compartible */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              URL para compartir con asistentes:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                value={shareableUrl}
                variant="outlined"
                size="small"
                InputProps={{ readOnly: true }}
              />
              <Button
                variant="contained"
                onClick={copyShareableUrl}
                startIcon={<ShareIcon />}
                sx={{ bgcolor: '#6164ff' }}
              >
                Copiar
              </Button>
              <Tooltip 
                title={getAvailableUserIndices().length === 0 ? "El acta no tiene correos registrados o todos los asistentes con correo ya firmaron. Edite el acta para agregar correos." : "Enviar solicitud de firma por correo"}
                placement="top"
              >
                <span>
                  <Button
                    variant="contained"
                    onClick={() => setShowEmailDialog(true)}
                    startIcon={<EmailIcon />}
                    sx={{ bgcolor: '#4caf50', ...(getAvailableUserIndices().length === 0 ? { pointerEvents: 'none' } : {}) }}
                    disabled={getAvailableUserIndices().length === 0}
                  >
                    Enviar
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Paper>

          {/* Botón para ver acta */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setShowActaPreview(true)}
              startIcon={<VisibilityIcon />}
              sx={{ color: '#6164ff', borderColor: '#6164ff' }}
            >
              Ver Acta
            </Button>
          </Box>

          {/* Lista de asistentes */}
          <Typography variant="h6" gutterBottom>
            Asistentes y Firmas
          </Typography>
          
          {acta?.asistentes?.map((asistente, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {asistente.nombre}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {asistente.cargo} - {asistente.asistio}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {signatures[index] ? (
                    <Chip
                      label="Firmado"
                      color="success"
                      size="small"
                    />
                  ) : asistente.autoApproved ? (
                    <Chip
                      label="Aprobado por tiempo"
                      color="warning"
                      size="small"
                    />
                  ) : (
                    <Chip
                      label="Sin firmar"
                      color="default"
                      size="small"
                    />
                  )}
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleAttendeeSelect(index)}
                    sx={{ bgcolor: '#6164ff' }}
                  >
                    {signatures[index] ? 'Refirmar' : 'Firmar'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          ))}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} variant="outlined">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de firma */}
      <Dialog
        open={showSignaturePad}
        onClose={() => setShowSignaturePad(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Firmar como: {acta?.asistentes?.[selectedAttendee]?.nombre}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs 
              value={signMethod} 
              onChange={(e, newValue) => setSignMethod(newValue)} 
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab icon={<DrawIcon />} label="Dibujar en Pantalla" />
              <Tab icon={<UploadIcon />} label="Subir Imagen" />
            </Tabs>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {signMethod === 0 
                ? 'Dibuje su firma en el área de abajo' 
                : 'Suba una imagen de su firma (PNG o JPG, máx 2MB)'}
            </Typography>
          </Box>
          
          {signMethod === 0 ? (
            <Paper sx={{ p: 2, border: '2px dashed #ccc', bgcolor: '#fafafa' }}>
              <SignatureCanvas
                ref={signatureRef}
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
              />
            </Paper>
          ) : (
            <Paper 
              sx={{ 
                p: 3, 
                border: '2px dashed #6164ff', 
                bgcolor: '#f5f6ff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '236px',
                cursor: !uploadedImage ? 'pointer' : 'default',
                transition: 'all 0.3s ease',
                '&:hover': !uploadedImage ? { bgcolor: '#eef0ff' } : {}
              }}
              onClick={() => !uploadedImage && fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
              />
              
              {uploadedImage ? (
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', bgcolor: '#fff', borderRadius: 1, p: 1 }}>
                    <img 
                      src={uploadedImage} 
                      alt="Firma subida" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '200px', 
                        objectFit: 'contain' 
                      }} 
                    />
                    <IconButton 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveUploadedImage();
                      }}
                      sx={{ 
                        position: 'absolute', 
                        top: -10, 
                        right: -10, 
                        bgcolor: 'error.main', 
                        color: 'white',
                        '&:hover': { bgcolor: 'error.dark' },
                        zIndex: 10
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<RotateLeftIcon />}
                      onClick={(e) => { e.stopPropagation(); handleRotate(-90); }}
                    >
                      Rotar Izq
                    </Button>
                    <Button 
                      variant="outlined" 
                      size="small" 
                      startIcon={<RotateRightIcon />}
                      onClick={(e) => { e.stopPropagation(); handleRotate(90); }}
                    >
                      Rotar Der
                    </Button>
                    <Button 
                      variant="contained" 
                      size="small" 
                      startIcon={<CropIcon />}
                      onClick={(e) => { e.stopPropagation(); handleAutoCrop(); }}
                      color="secondary"
                    >
                      Auto Ajustar
                    </Button>
                    <Button 
                      variant="text" 
                      size="small" 
                      startIcon={<ResetIcon />}
                      onClick={(e) => { e.stopPropagation(); handleResetImage(); }}
                      color="error"
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center' }}>
                  <UploadIcon sx={{ fontSize: 48, color: '#6164ff', mb: 1 }} />
                  <Typography variant="h6" color="primary" gutterBottom>
                    Haga clic para subir imagen
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    Formatos soportados: PNG, JPG
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSignaturePad(false)}>
            Cancelar
          </Button>
          {signMethod === 0 && (
            <Button onClick={handleClearSignature} startIcon={<ClearIcon />}>
              Limpiar
            </Button>
          )}
          <Button
            onClick={handleSaveSignature}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ bgcolor: '#6164ff' }}
            disabled={signMethod === 1 && !uploadedImage}
          >
            Guardar Firma
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de vista previa del acta */}
      <Dialog
        open={showActaPreview}
        onClose={() => setShowActaPreview(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { height: isMobile ? '100%' : '90vh' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#6164ff',
          color: 'white'
        }}>
          <Typography variant="h6" component="div">Vista Previa del Acta</Typography>
          <IconButton
            onClick={() => setShowActaPreview(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {showActaPreview && acta && (
            <Box sx={{ height: '100%', width: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
              <BlobProvider document={<ActaPDF data={acta} />}>
                {({ blob, url, loading, error }) => {
                  if (loading) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Generando documento...</Typography>
                      </Box>
                    );
                  }
                  if (error) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                        <Typography color="error">Error al generar el PDF: {error.message}</Typography>
                      </Box>
                    );
                  }
                  return (
                    <iframe
                      src={url}
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                        flexGrow: 1
                      }}
                      title="Vista Previa del Acta"
                    />
                  );
                }}
              </BlobProvider>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo del Tutorial */}
      <Dialog
        open={showTutorial}
        onClose={() => setShowTutorial(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#6164ff',
          color: 'white'
        }}>
          <Typography variant="h6">
            Tutorial: Cómo Firmar un Acta
          </Typography>
          <IconButton onClick={() => setShowTutorial(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sigue estos pasos para firmar digitalmente:
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            border: '2px solid #e0e0e0',
            borderRadius: 2,
            p: 2,
            bgcolor: '#f9f9f9'
          }}>
            <img 
              src="https://res.cloudinary.com/dvmkjwx94/image/upload/v1751068105/w5pp29qmyjfo1s61mfrc.gif" 
              alt="Tutorial de firma" 
              style={{ 
                maxWidth: '100%', 
                height: 'auto',
                borderRadius: '8px'
              }} 
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTutorial(false)} variant="contained">
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de envío de correos */}
      <Dialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: '#4caf50',
          color: 'white'
        }}>
          <Typography variant="h6">
            Enviar Correos de Firma
          </Typography>
          <IconButton onClick={() => setShowEmailDialog(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione los usuarios que recibirán el correo con el enlace para firmar:
          </Typography>
          
          {getAvailableUserIndices().length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No hay usuarios disponibles para enviar correos.
              (Solo se pueden seleccionar usuarios con email que no hayan firmado)
            </Typography>
          ) : (
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {getAvailableUserIndices().map((originalIndex) => {
                const asistente = acta.asistentes[originalIndex];
                return (
                  <Paper 
                    key={originalIndex} 
                    sx={{ 
                      p: 2, 
                      mb: 1, 
                      border: selectedUsers.includes(originalIndex) ? '2px solid #4caf50' : '1px solid #e0e0e0',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#f5f5f5' }
                    }}
                    onClick={() => handleUserSelection(originalIndex)}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {asistente.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {asistente.cargo}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {asistente.email}
                        </Typography>
                      </Box>
                      <Box>
                        {selectedUsers.includes(originalIndex) ? (
                          <Chip
                            label="Seleccionado"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Seleccionar"
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
          
          {selectedUsers.length > 0 && (
            <Typography variant="body2" sx={{ mt: 2, color: '#4caf50' }}>
              {selectedUsers.length} usuario(s) seleccionado(s)
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setShowEmailDialog(false);
              setSelectedUsers([]);
            }} 
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSendEmails}
            variant="contained"
            startIcon={<EmailIcon />}
            sx={{ bgcolor: '#4caf50' }}
            disabled={selectedUsers.length === 0 || emailLoading}
          >
            {emailLoading ? 'Enviando...' : `Enviar Correos (${selectedUsers.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignatureManager;
